<template>
  <div class="h-full flex flex-col">
    <Component
      :is="selectedView"
      :count="count"
      :fetchingStatus="fetchingStatus"
      v-bind="
        ['table', 'list'].includes(viewType) ? { isSelectable: true } : {}
      "
      @show:detail="emit('show:detail', $event)"
      @update:count="emit('update:count', $event)"
      @update:fetchingStatus="emit('update:fetchingStatus', $event)"
    >
      <template
        v-slot:state
        v-if="count !== null || fetchingStatus === 'error'"
      >
        <State
          :type="state.type"
          :title="state.title"
          :text="state.text"
          :button="state.button"
          :action="state.action"
          @click:button="handleStateEmit(state.emit)"
        />
      </template>
      <template #bulk-actions="{ selectedTasks }">
        <TasksBulkActions
          :selectedTasks="selectedTasks"
          :class="{ 'mb-18': viewType === 'table' }"
        />
      </template>
    </Component>
  </div>
</template>

<script setup>
// Import Vue Utilities
import { computed, inject, watch } from "vue";

// Import Utilities
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";

// Import Components
import TasksKanban from "@/Pages/Tasks/TasksKanban";
import TasksDataGrid from "@/Pages/Tasks/TasksDataGrid";
import { CollapsibleView } from "@/SharedComponents/Tasks/CollapsibleView";
import { State } from "@/SharedComponents/State";
import TasksBulkActions from "@/Pages/Tasks/TasksBulkActions";

// Import Composables
import { useSearch } from "@/Composables/useSearch";
import { useSorting } from "@/Composables/useSorting";
import { useFilters } from "@/Composables/useFilters";
import { useUserPreferences } from "@/Composables/useUserPreferences";

// Emits
const emit = defineEmits([
  "show:detail",
  "update:count",
  "update:fetchingStatus",
  "add:task",
  "reset",
]);

// Emitter
const emitter = inject("emitter");

// Task View
const { data: viewType } = useUserPreferences("task_view");

// Search: Tasks
const searchStore = useSearch("tasks");
const { searchValues, query } = storeToRefs(searchStore);

// Sorting: Tasks
const sortingStore = useSorting("tasks");
const { sortingValues } = storeToRefs(sortingStore);

// Filters: Tasks
const filterStore = useFilters("tasks");
const { filterValues, isFiltered } = storeToRefs(filterStore);

// Filters: Global
const globalFilterStore = useFilters("global");
const { filterValues: globalFilterValues } = storeToRefs(globalFilterStore);

// Translations
const { t } = useI18n();

// Define Props
const props = defineProps({
  count: { type: Number, default: 0 },
  fetchingStatus: { type: String, required: true },
});

// Computed Props
const filters = computed(() => {
  return {
    ...sortingValues.value,
    ...searchValues.value,
    ...filterValues.value,
    tenant: globalFilterValues.value.tenant,
    tenant_only: filterValues.value.tenant?.length > 0,
  };
});

// Watchers
watch(
  filters,
  () => {
    emitter.emit("reload-tasks");
  },
  {
    deep: true,
  }
);

const selectedView = computed(() => {
  switch (viewType.value) {
    case "kanban":
      return TasksKanban;
    case "table":
      return TasksDataGrid;
    default:
      return CollapsibleView;
  }
});

const state = computed(() => {
  let type = "Tasks";
  let title = t("components.state.no_tasks.title");
  let text = t("components.state.no_tasks.text");
  let button = t("components.state.no_tasks.button");
  let emit = "add:task";
  let action = "add-task";

  if (query.value || isFiltered.value) {
    type = "NoResults";
    title = t("components.state.no_results.title");
    text = t("components.state.no_results.text");
    button = t("components.state.no_results.button");
    emit = "reset";
    action = "reset";
  }

  if (props.fetchingStatus === "error") {
    type = "Error";
    title = t("components.state.error.title");
    text = t("components.state.error.text");
    button = t("components.state.error.button");
    emit = "reload";
    action = "reload";
  }

  return {
    type: type,
    title: title,
    text: text,
    button: button,
    emit: emit,
    action: action,
  };
});

// Functions
const handleStateEmit = (value) => {
  emit(value);
};
</script>
